import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { io, Socket } from 'socket.io-client';
import { API_BASE } from '../api/client';

// Desktop has VITE_API_URL set; web app relies on same-origin cookies (no JS token access needed)
const IS_DESKTOP = !!import.meta.env.VITE_API_URL;

type WsEvent =
  | 'invoice:changed'
  | 'payment:changed'
  | 'purchase:changed'
  | 'product:changed'
  | 'expense:changed'
  | 'party:changed'
  | 'company:changed';

const EVENT_KEYS: Record<WsEvent, string[]> = {
  'invoice:changed':  ['invoices', 'dashboard'],
  'payment:changed':  ['payments', 'invoices', 'dashboard'],
  'purchase:changed': ['purchases', 'dashboard'],
  'product:changed':  ['products'],
  'expense:changed':  ['expenses', 'dashboard'],
  'party:changed':    ['parties'],
  'company:changed':  ['profile', 'settings'],
};

export function useRealtimeSync(tenantId?: string) {
  const qc = useQueryClient();
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    // Strip /v1 suffix to get the base server URL
    const serverUrl = API_BASE.replace(/\/v1\/?$/, '');

    // Desktop: pass Bearer token so the gateway can verify identity.
    // Web: HttpOnly cl_token cookie is sent automatically on same-origin WS upgrade — no JS access needed.
    const desktopToken = IS_DESKTOP ? (sessionStorage.getItem('cl_token') ?? undefined) : undefined;

    const socket = io(serverUrl, {
      transports: ['websocket', 'polling'],
      auth: desktopToken ? { token: desktopToken } : {},
      reconnectionDelay: 1000,
      reconnectionDelayMax: 30_000,
      reconnectionAttempts: Infinity,
      randomizationFactor: 0.5,
    });


    socketRef.current = socket;

    const events = Object.keys(EVENT_KEYS) as WsEvent[];
    for (const event of events) {
      socket.on(event, () => {
        for (const key of EVENT_KEYS[event]) {
          qc.invalidateQueries({ queryKey: [key] });
        }
      });
    }

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [qc, tenantId]);
}
